import {
	ActionIcon,
	Box,
	createStyles,
	getStylesRef,
	Group,
	Menu,
	Stack,
} from '@mantine/core';
import { Icon, Text } from '@repo/foundations';
import { useMemo } from 'react';
import { getAgentListItemHumanReadableSchedule } from '../../../pages/AIAgentsPage/utils';
import type { AgentListItemProps } from '../types';

const useStyles = createStyles((theme) => ({
	agentListItem: {
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: `${theme.spacing['xs']} ${theme.spacing['4xs']} ${theme.spacing['xs']} ${theme.spacing.sm}`,
		gap: theme.spacing.xs,
		borderRadius: theme.radius.sm,
		backgroundColor: theme.other.getColor('fill/transparent/default'),

		'&:hover': {
			backgroundColor: theme.other.getColor('fill/transparent/hover'),
			[`& .${getStylesRef('menuButton')}`]: {
				opacity: 1,
				width: 'auto',
			},
		},
	},
	titleArea: {
		width: '100%',
		minWidth: 0,
	},
	menuButton: {
		ref: getStylesRef('menuButton'),
		opacity: 0,
		width: 0,
		marginRight: theme.spacing['2xs'],
	},
}));

export function AgentListItem({
	agent,
	onRun,
	onEdit,
	onViewHistory,
	onDelete,
	onTogglePause,
}: AgentListItemProps) {
	const { classes } = useStyles();
	const schedule = useMemo(
		() =>
			getAgentListItemHumanReadableSchedule(agent.cron_expression || undefined),
		[agent.cron_expression]
	);

	return (
		<Box className={classes.agentListItem}>
			<Group spacing="sm" noWrap className={classes.titleArea}>
				<Stack spacing={0} className={classes.titleArea}>
					<Text size="sm" weight="bold" truncate>
						{agent.title}
					</Text>
					<Text size="xs" color="text/secondary/default" truncate maw={300}>
						{agent.is_enabled ? schedule : 'Paused'}
					</Text>
				</Stack>
			</Group>
			<Menu shadow="md" offset={2}>
				<Menu.Target>
					<ActionIcon
						size="xs"
						className={classes.menuButton}
						onClick={(e) => e.stopPropagation()}
					>
						<Icon name="dots" size="md" />
					</ActionIcon>
				</Menu.Target>
				<Menu.Dropdown>
					<Menu.Item
						onClick={(e) => {
							e.stopPropagation();
							onRun(agent);
						}}
					>
						<Group spacing="2xs">
							<Icon name="playerPlay" />
							<Text size="sm">Run now</Text>
						</Group>
					</Menu.Item>
					<Menu.Item
						onClick={(e) => {
							e.stopPropagation();
							onViewHistory(agent);
						}}
					>
						<Group spacing="2xs">
							<Icon name="history" />
							<Text size="sm">View run history</Text>
						</Group>
					</Menu.Item>
					<Menu.Divider />
					<Menu.Item
						onClick={(e) => {
							e.stopPropagation();
							onEdit(agent);
						}}
					>
						<Group spacing="2xs">
							<Icon name="pencil" />
							<Text size="sm">Edit</Text>
						</Group>
					</Menu.Item>
					<Menu.Item
						onClick={(e) => {
							e.stopPropagation();
							onTogglePause(agent);
						}}
					>
						<Group spacing="2xs">
							<Icon name={'playerPause'} />
							<Text size="sm">{agent.is_enabled ? 'Pause' : 'Unpause'}</Text>
						</Group>
					</Menu.Item>
					<Menu.Divider />
					<Menu.Item
						onClick={(e) => {
							e.stopPropagation();
							onDelete(agent);
						}}
					>
						<Group spacing="2xs">
							<Icon name="trash" />
							<Text size="sm">Delete...</Text>
						</Group>
					</Menu.Item>
				</Menu.Dropdown>
			</Menu>
		</Box>
	);
}
